import { useEffect, useRef, useState, useCallback } from 'react';
import styles from './styles.module.scss';
import * as THREE from 'three';
import {
	computeBoundsTree,
	disposeBoundsTree,
	computeBatchedBoundsTree,
	disposeBatchedBoundsTree,
	acceleratedRaycast,
} from 'three-mesh-bvh';
import { updateLighting } from '@/utils/lighting';
import { createCameraSystem, createMouseHandlers, MouseHandlers } from '@/utils/cameraControls';
import { setupRenderer, setupLights, RENDERER_CONFIG } from '@/utils/threeSetup';

import { useDailySunlight } from '@/hooks/useDailySunlight';
import { useFacadeSunlight } from '@/hooks/useFacadeSunlight';
import { useClickHandler } from '@/hooks/useClickHandler';
import { usePaintHandler } from '@/hooks/usePaintHandler';
import { useHandleDragHandler } from '@/hooks/useHandleDragHandler';
import { useWindowDragPlacementHandler } from '@/hooks/useWindowDragPlacementHandler';
import { useViewAnalysisPlaneControls } from '@/hooks/useViewAnalysisPlaneControls';
import { ControlPanel } from '@/components/ControlPanel/ControlPanel';

import { DailySunlightResult } from '@/types/sunlight';
import { getBuildingGraph, highlightSelectedBuilding, removeSelectedBuildingHighlight } from '@/utils/visualization';
import { getSceneWithTerrain } from '@/utils/sceneGeneration';
import {
	buildSceneBVH,
	setPhotonEnergyHeatmapFacadeBuildingPath,
	setPhotonVisualizationInteractionMode,
	updateSunPositionVisualization,
} from '@/utils/rayTracing';
import { loadViewFeaturesFromStorage } from '@/utils/viewFeatures';
import {
	setSamplingPlaneBlockingMode,
	setSamplingPlaneCategoryFilter,
	setSamplingPlaneVisualizationMode,
	removeViewLineById,
} from '@/utils/viewAnalyser';
import { initializeViewFeaturesPersistence } from '@/services/viewFeaturesPersistence';
import { SelectedViewPlaneStatsPanel } from './SelectedViewPlaneStatsPanel';

import { Forma } from 'forma-embedded-view-sdk/auto';
import {
	OrthoPreviewCamera,
	renderOrthoPreview,
	updateOrthoPreviewOnResize,
} from '@/utils/orthographicCamera';
import { WeaveButton } from '../../UI/WeaveButton/WeaveButton';
import { Representation } from 'forma-embedded-view-sdk/dist/internal/representations';
import { FormaCompass } from '../../UI/FormaCompass/FormaCompass';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Stats from 'three/examples/jsm/libs/stats.module';
import { SunlightTooltip } from '../../SunlightTooltip/SunlightTooltip';
import { FacadeTooltip } from '../../FacadeTooltip/FacadeTooltip';
import { SunlightBarChart } from '@/components/SunlightBarChart/SunlightBarChart';
import { WorkflowProps } from '@/types/workflow';
import { WeaveBanner } from '@/components/UI/WeaveBanner/WeaveBanner';
import SkeletonLoader from '@/components/UI/SkeletonLoader/SkeletonLoader';
import { ThreeControls } from '@/components/ThreeControls/ThreeControls';
import { StatusMessage } from '@/components/StatusMessage/StatusMessage';
import { FullScreenButton } from '@/components/UI/FullScreenButton/FullScreenButton';
import { AnalysisTab } from '@/components/home/home';
import { WeaveAccordion } from '@/components/UI/WeaveAccordion/WeaveAccordion';
import { WeaveIconButton } from '@/components/UI/WeaveIconButton/WeaveIconButton';
import { Icon } from '@/components/UI/Icon/Icon';
import { useWorkflowStore } from '@/store/workflowStore';

declare global {
	interface Window {
		dailySunlightResults?: DailySunlightResult[];
	}
}

// Add the extension functions
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

THREE.BatchedMesh.prototype.computeBoundsTree = computeBatchedBoundsTree;
THREE.BatchedMesh.prototype.disposeBoundsTree = disposeBatchedBoundsTree;
THREE.BatchedMesh.prototype.raycast = acceleratedRaycast;

const ThreeScene = ({ onDataChange, workflowData, activeSection, tab }: WorkflowProps & { tab: AnalysisTab }) => {
	const threeCanvasRef = useRef<HTMLCanvasElement>(null);
	const sceneRef = useRef<THREE.Scene | null>(null);
	const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
	const ambientLightRef = useRef<THREE.AmbientLight | null>(null);
	const directionalLightRef = useRef<THREE.DirectionalLight | null>(null);
	const starsRef = useRef<THREE.Points | null>(null);
	const currentSunPositionRef = useRef<THREE.Vector3>(new THREE.Vector3());
	const cameraRef = useRef<THREE.Camera | null>(null);
	const mouseHandlersRef = useRef<MouseHandlers | null>(null);
	const orthoPreviewRef = useRef<OrthoPreviewCamera>(new OrthoPreviewCamera());
	const orthoPreviewRendererRef = useRef<THREE.WebGLRenderer | null>(null);
	const statsRef = useRef<Stats | null>(null);
	const orthoPreviewCanvasRef = useRef<HTMLCanvasElement>(null);
	const cameraSystemRef = useRef<{
		controls: OrbitControls;
	} | null>(null);
	const selectedBuildingRef = useRef<THREE.Object3D | null>(null);
	const selectedBuildingPathRef = useRef<string | null>(null);
	const canvasContainerRef = useRef<HTMLDivElement>(null);
	const hasPhotonSceneRebuildRef = useRef(false);
	const {
		isHoleCreationMode,
		isHoleDeletionMode,
		showStats,
		selectedViewPlaneStats,
		viewPlaneVisualizationMode,
		setViewPlaneVisualizationMode,
		viewPlaneBlockingMode,
		setViewPlaneBlockingMode,
		viewPlaneCategoryFilter,
		setViewPlaneCategoryFilter,
		viewPlaneInspector,
		viewPlaneSampleScale,
		setViewPlaneSampleScale,
	} = useThreeSceneStore();

	const [currentDate, setCurrentDate] = useState(new Date());
	const [currentTime, setCurrentTime] = useState('19:00');
	const [latitude, setLatitude] = useState<number | null>(null);
	const [longitude, setLongitude] = useState<number | null>(null);
	const [sceneInitialized, setSceneInitialized] = useState(false);
	const [canvasHeight, setCanvasHeight] = useState<number>(RENDERER_CONFIG.canvasHeight);
	const [error, setError] = useState<string | null>(null);
	const [showOrthoPreview, setShowOrthoPreview] = useState(false);
	const [sceneVersion, setSceneVersion] = useState(0);
	const [showBarChart, setShowBarChart] = useState(false);
	const [buildingGraph, setBuildingGraph] = useState<Representation.GraphBuilding | null>(null);
	const [currentLevelIndex, setCurrentLevelIndex] = useState<number | null>(null);
	const { areEventsPaused } = useThreeSceneStore();
	const { activeObjectContext } = useWorkflowStore();

	const calculateCanvasHeight = useCallback(() => {
		if (!canvasContainerRef.current) return RENDERER_CONFIG.canvasHeight;

		// Get the container's bounding rect
		const container = canvasContainerRef.current;
		const rect = container.getBoundingClientRect();

		// Calculate available height from container top to viewport bottom
		// Subtract some padding for bottom actions bar (approximately 60px)
		const availableHeight = window.innerHeight - rect.top - 60;

		// Ensure minimum height of 650px, max of 1200px
		const newHeight = Math.max(650, Math.min(availableHeight, 1200));
		return newHeight;
	}, []);

	const updateCanvasHeight = useCallback(() => {
		const newHeight = calculateCanvasHeight();
		setCanvasHeight(newHeight);

		// If scene is already initialized, update renderer size
		if (rendererRef.current && cameraRef.current && threeCanvasRef.current) {
			const container = threeCanvasRef.current.parentElement;
			if (container) {
				const width = container.clientWidth;
				rendererRef.current.setSize(width, newHeight);
				if (cameraRef.current instanceof THREE.PerspectiveCamera) {
					cameraRef.current.aspect = width / newHeight;
					cameraRef.current.updateProjectionMatrix();
				}
			}
		}
	}, [calculateCanvasHeight]);

	const getLocation = useCallback(async () => {
		try {
			const location = await Forma.project.getGeoLocation();
			if (location) {
				const [latitude, longitude] = location;
				setLatitude(latitude);
				setLongitude(longitude);
			}
		} catch (error) {
			console.error('Error fetching project geolocation:', error);
			setError('Failed to fetch project geolocation.');
		}
	}, []);

	const dailySunlight = useDailySunlight({
		sceneRef,
		starsRef,
		latitude,
		longitude,
	});

	const facadeSunlight = useFacadeSunlight({
		sceneRef,
	});

	const handleBuildingClick = useCallback(
		async (building: THREE.Object3D) => {
			if (isHoleCreationMode) return;
			if (isHoleDeletionMode) return;
			const graph = await getBuildingGraph(building);
			if (!graph) {
				console.warn('No building graph found for selected building');
				return;
			}

			setBuildingGraph(graph || null);
			orthoPreviewRef.current.removeViewPointTargets(sceneRef.current!);

			// Store selected building reference
			selectedBuildingRef.current = building;
			selectedBuildingPathRef.current =
				typeof building.userData?.path === 'string' ? building.userData.path : null;
			setPhotonEnergyHeatmapFacadeBuildingPath(selectedBuildingPathRef.current);

			if (sceneRef.current) {
				highlightSelectedBuilding(building, sceneRef.current);
			}

			if (graph) {
				if (tab.id === 'daylight-views') {
					orthoPreviewRef.current.addViewPointTargets(sceneRef.current!);
				}
				orthoPreviewRef.current.clearTarget();
				orthoPreviewRef.current.setTarget(
					building,
					graph,
					sceneRef.current || undefined,
					sceneRef.current?.getObjectByName('terrain') || undefined
				);

				setShowOrthoPreview(true);
			}
		},
		[isHoleCreationMode, isHoleDeletionMode, tab.id]
	);

	useEffect(() => {
		if (sceneInitialized && sceneRef.current) {
			// if there's an active object context, try to find the corresponding object in the scene and set it as the target for the orthographic preview
			if (activeObjectContext) {
				const { contextKey } = activeObjectContext;
				sceneRef.current.traverse((obj) => {
					if (obj.name.startsWith('building_')) {
						if (obj.name.includes(contextKey)) {
							handleBuildingClick(obj);
						}
					}
				});
			}
		}
	}, [sceneInitialized, activeObjectContext, handleBuildingClick, sceneVersion]);

	const handleClearPreview = useCallback(() => {
		orthoPreviewRef.current.clearTarget(sceneRef.current!);
		orthoPreviewRef.current.removeViewPointTargets(sceneRef.current!);
		orthoPreviewRef.current.removeRoomFloorPolygons(sceneRef.current!);

		// Clear selected building reference
		selectedBuildingRef.current = null;
		selectedBuildingPathRef.current = null;
		setPhotonEnergyHeatmapFacadeBuildingPath(null);
		if (sceneRef.current) {
			removeSelectedBuildingHighlight(sceneRef.current);
			facadeSunlight.clearFacadeVisualization();
			dailySunlight.clearAllVisualizations();
		}

		setShowOrthoPreview(false);
	}, [facadeSunlight, dailySunlight]);

	const getCurrentDateTime = useCallback(async () => {
		const projectDate = await Forma.sun.getDate();

		setCurrentDate(projectDate ? new Date(projectDate) : new Date());
		setCurrentTime(
			projectDate ? new Date(projectDate).toISOString().substring(11, 16) : '12:00'
		);
	}, []);

	const clickHandler = useClickHandler({
		threeCanvasRef,
		starsRef,
		isHoleCreationMode,
		isHoleDeletionMode,
		currentSunPositionRef,
		directionalLightRef,
		ambientLightRef,
		rendererRef,
		onBuildingClick: handleBuildingClick,
		tab,
	});

	// Initialize paint handler for terrain painting
	usePaintHandler({
		canvasRef: threeCanvasRef,
		cameraRef,
		sceneRef,
		sceneInitialized,
	});

	useHandleDragHandler({
		canvasRef: threeCanvasRef,
		cameraRef,
		sceneRef,
		controlsRef: cameraSystemRef,
		sceneInitialized,
		tab
	});

	useWindowDragPlacementHandler({
		canvasRef: threeCanvasRef,
		cameraRef,
		sceneRef,
		enabled: isHoleCreationMode,
	});

	useViewAnalysisPlaneControls({
		canvasRef: threeCanvasRef,
		cameraRef,
		sceneRef,
		controlsRef: cameraSystemRef,
		sceneInitialized,
		sceneVersion,
		tab,
	});

	const initializeLighting = useCallback(() => {
		if (!sceneRef.current) return;
		const { ambientLight, directionalLight } = setupLights(
			sceneRef.current,
			rendererRef.current!,
			currentSunPositionRef.current
		);
		ambientLightRef.current = ambientLight;
		directionalLightRef.current = directionalLight;

		updateLighting(
			sceneRef.current,
			ambientLight,
			directionalLight,
			rendererRef.current!,
			starsRef.current,
			currentSunPositionRef,
			currentDate,
			currentTime,
			latitude,
			longitude
		);

		updateSunPositionVisualization(sceneRef.current, currentSunPositionRef.current);
	}, [currentDate, currentTime, latitude, longitude]);

	const rebuildSceneForPhotonMapping = useCallback(
		async (enableExtrudedWalls: boolean) => {
			const preservedBuildingPath = enableExtrudedWalls
				? selectedBuildingPathRef.current
				: null;
			const rebuiltScene = await getSceneWithTerrain(enableExtrudedWalls);
			buildSceneBVH(rebuiltScene);
			sceneRef.current = rebuiltScene;
			await loadViewFeaturesFromStorage(rebuiltScene).catch((error) => {
				console.error('Failed to load view features after scene rebuild:', error);
			});

			// Rebuild invalidates selected objects and preview state.
			selectedBuildingRef.current = null;
			selectedBuildingPathRef.current = preservedBuildingPath;
			setPhotonEnergyHeatmapFacadeBuildingPath(preservedBuildingPath);
			setBuildingGraph(null);
			setCurrentLevelIndex(null);
			setShowOrthoPreview(false);
			setShowBarChart(false);
			removeSelectedBuildingHighlight(rebuiltScene);

			initializeLighting();
			setSceneVersion((prev) => prev + 1);
		},
		[initializeLighting]
	);

	const handleBeforeRunPhotonMapping = useCallback(async () => {
		if (!hasPhotonSceneRebuildRef.current) {
			await rebuildSceneForPhotonMapping(true);
			hasPhotonSceneRebuildRef.current = true;
		}
	}, [rebuildSceneForPhotonMapping]);

	const handleAfterClearPhotons = useCallback(async () => {
		const extrudedWalls = useThreeSceneStore.getState().hasExtrudedWalls;
		if (!extrudedWalls) {
			return;
		}

		await rebuildSceneForPhotonMapping(false);
		hasPhotonSceneRebuildRef.current = false;
	}, [rebuildSceneForPhotonMapping]);

	// Initialize auto-save for view features (once on mount)
	useEffect(() => {
		initializeViewFeaturesPersistence(sceneRef);
	}, []);

	useEffect(() => {
		getCurrentDateTime();
		if (latitude === null || longitude === null) return;
		let cleanup: (() => void) | null = null;
		let animationId: number | null = null;
		let lastFrameTime = 0;
		const targetFPS = RENDERER_CONFIG.targetFPS;
		const frameDuration = 1000 / targetFPS;

		const initScene = async () => {
			if (!threeCanvasRef.current) return;
			setSceneInitialized(false);
			try {
				let controlsIdleTimeout: ReturnType<typeof setTimeout> | null = null;
				let photonInteractionActive = false;
				const scene = await getSceneWithTerrain();
				buildSceneBVH(scene);

				sceneRef.current = scene;

				const renderer = setupRenderer(threeCanvasRef.current);
				rendererRef.current = renderer;

				const cameraSystem = await createCameraSystem(
					renderer,
					scene,
					threeCanvasRef.current,
					canvasHeight,
					{ onDataChange, workflowData, activeSection, sectionLabel: 'Daylight Factor & Views Analysis' }
				);
				const { camera, controls, mouseHandlers, keyboardHandlers, resizeHandler } =
					cameraSystem;
				const beginPhotonInteraction = () => {
					if (!photonInteractionActive) {
						photonInteractionActive = true;
						setPhotonVisualizationInteractionMode(true);
					}
					if (controlsIdleTimeout) {
						clearTimeout(controlsIdleTimeout);
						controlsIdleTimeout = null;
					}
				};
				const schedulePhotonInteractionEnd = () => {
					if (controlsIdleTimeout) {
						clearTimeout(controlsIdleTimeout);
					}
					controlsIdleTimeout = setTimeout(() => {
						photonInteractionActive = false;
						setPhotonVisualizationInteractionMode(false);
						controlsIdleTimeout = null;
					}, 30);
				};
				const handleControlsStart = () => {
					beginPhotonInteraction();
				};
				const handleControlsChange = () => {
					if (photonInteractionActive) {
						schedulePhotonInteractionEnd();
					}
				};
				const handleControlsEnd = () => {
					schedulePhotonInteractionEnd();
				};
				controls.addEventListener('start', handleControlsStart);
				controls.addEventListener('change', handleControlsChange);
				controls.addEventListener('end', handleControlsEnd);
				cameraSystemRef.current = { controls };
				cameraRef.current = camera;
				mouseHandlersRef.current = mouseHandlers;

				const canvas = renderer.domElement;
				const handleWheelZoom = () => {
					beginPhotonInteraction();
					schedulePhotonInteractionEnd();
				};

				canvas.addEventListener('mousedown', mouseHandlers.handleMouseDown);
				canvas.addEventListener('mousemove', mouseHandlers.handleMouseMove);
				canvas.addEventListener('mouseup', mouseHandlers.handleMouseUp);
				canvas.addEventListener('mouseleave', mouseHandlers.handleMouseLeave);
				canvas.addEventListener('wheel', handleWheelZoom, { passive: true });
				const handleWindowResize = () => {
					updateCanvasHeight();
					resizeHandler.handleResize();
				};

				window.addEventListener('resize', handleWindowResize);
				window.addEventListener('keydown', keyboardHandlers.handleKeyDown);
				window.addEventListener('keyup', keyboardHandlers.handleKeyUp);

				// Trigger initial resize to ensure correct dimensions
				setTimeout(() => updateCanvasHeight(), 100);

				const animate = (now?: number) => {
					animationId = requestAnimationFrame(animate);
					const currentTime = now ?? performance.now();
					const timeSinceLastFrame = currentTime - lastFrameTime;
					if (timeSinceLastFrame < frameDuration) return;

					// Sync to current time if too far behind, otherwise increment
					lastFrameTime = timeSinceLastFrame > frameDuration * 2 ? currentTime : lastFrameTime + frameDuration;
					controls.update();

					if (statsRef.current && showStats) {
						statsRef.current.update();
					}

					const renderScene = sceneRef.current || scene;
					renderer.render(renderScene, camera);
					if (
						orthoPreviewRendererRef.current &&
						orthoPreviewRef.current &&
						orthoPreviewRef.current.targetObject
					) {
						renderOrthoPreview(
							orthoPreviewRendererRef.current,
							200,
							200,
							orthoPreviewRef.current
						);
					}
				};
				animate();
				initializeLighting();

				// Initialize Stats (FPS monitor)
				const stats = new Stats();
				stats.dom.style.position = 'absolute';
				stats.dom.style.top = '10px';
				stats.dom.style.left = 'auto';
				stats.dom.style.right = '10px';
				stats.dom.style.width = '80px';
				stats.dom.style.display = showStats ? 'block' : 'none';
				if (threeCanvasRef.current?.parentElement) {
					threeCanvasRef.current.parentElement.appendChild(stats.dom);
					statsRef.current = stats;
				}

				setSceneInitialized(true);

				cleanup = () => {
					if (animationId) {
						cancelAnimationFrame(animationId);
					}
					// Remove Stats DOM element
					if (statsRef.current?.dom) {
						statsRef.current.dom.remove();
						statsRef.current = null;
					}

					if (scene) {
						removeSelectedBuildingHighlight(scene);
					}
					// Clean removal of event listeners
					canvas.removeEventListener('mousedown', mouseHandlers.handleMouseDown);
					canvas.removeEventListener('mousemove', mouseHandlers.handleMouseMove);
					canvas.removeEventListener('mouseup', mouseHandlers.handleMouseUp);
					canvas.removeEventListener('mouseleave', mouseHandlers.handleMouseLeave);
					canvas.removeEventListener('wheel', handleWheelZoom);
					window.removeEventListener('resize', handleWindowResize);
					window.removeEventListener('keydown', keyboardHandlers.handleKeyDown);
					window.removeEventListener('keyup', keyboardHandlers.handleKeyUp);
					controls.removeEventListener('start', handleControlsStart);
					controls.removeEventListener('change', handleControlsChange);
					controls.removeEventListener('end', handleControlsEnd);
					if (controlsIdleTimeout) {
						clearTimeout(controlsIdleTimeout);
						controlsIdleTimeout = null;
					}
					photonInteractionActive = false;
					setPhotonVisualizationInteractionMode(false);
					keyboardHandlers.cleanup();
					resizeHandler.cleanup();
					controls.dispose();
				};
			} catch (error) {
				console.error('Error initializing ThreeScene:', error);
			}
		};

		initScene();

		return () => {
			if (cleanup) {
				cleanup();
			}
			if (animationId) {
				cancelAnimationFrame(animationId);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [latitude, longitude]);

	// Calculate canvas height on mount and when container ref is available
	useEffect(() => {
		if (canvasContainerRef.current) {
			updateCanvasHeight();
		}
	}, [updateCanvasHeight]);

	useEffect(() => {
		const { sunlightResultsRooms } = useDailySunlightStore.getState();
		setShowBarChart(!!sunlightResultsRooms && sunlightResultsRooms.length > 0);
	}, []);

	useEffect(() => {
		setSamplingPlaneVisualizationMode(viewPlaneVisualizationMode);
	}, [viewPlaneVisualizationMode]);

	useEffect(() => {
		setSamplingPlaneCategoryFilter(viewPlaneCategoryFilter);
	}, [viewPlaneCategoryFilter]);

	useEffect(() => {
		setSamplingPlaneBlockingMode(viewPlaneBlockingMode);
	}, [viewPlaneBlockingMode]);

	useEffect(() => {
		// Toggle stats display when showStats changes
		if (statsRef.current?.dom) {
			statsRef.current.dom.style.display = showStats ? 'block' : 'none';
		}
	}, [showStats]);

	useEffect(() => {
		const unsubscribe = useDailySunlightStore.subscribe((state) => {
			setShowBarChart(!!state.sunlightResultsRooms && state.sunlightResultsRooms.length > 0);
		});
		return () => unsubscribe();
	}, []);

	useEffect(() => {
		const orthoPreview = orthoPreviewRef.current;
		window.addEventListener('resize', () => updateOrthoPreviewOnResize(orthoPreview));
		updateOrthoPreviewOnResize(orthoPreview);

		return () => {
			window.removeEventListener('resize', () => updateOrthoPreviewOnResize(orthoPreview));
		};
	}, []);

	useEffect(() => {
		if (orthoPreviewCanvasRef.current && !orthoPreviewRendererRef.current) {
			const renderer = setupRenderer(orthoPreviewCanvasRef.current, {
				width: 200,
				height: 200,
			});

			orthoPreviewRendererRef.current = renderer;
		}
	}, [currentDate, currentTime, latitude, longitude, sceneInitialized]);

	useEffect(() => {
		if (!sceneInitialized || !rendererRef.current || !sceneRef.current || !cameraRef.current) {
			return;
		}

		const canvas = rendererRef.current.domElement;
		const previousHandlers = mouseHandlersRef.current;
		if (previousHandlers) {
			canvas.removeEventListener('mousedown', previousHandlers.handleMouseDown);
			canvas.removeEventListener('mousemove', previousHandlers.handleMouseMove);
			canvas.removeEventListener('mouseup', previousHandlers.handleMouseUp);
			canvas.removeEventListener('mouseleave', previousHandlers.handleMouseLeave);
		}

		const nextHandlers = createMouseHandlers(
			rendererRef.current,
			sceneRef.current,
			cameraRef.current,
			{
				onDataChange,
				workflowData,
				activeSection,
				sectionLabel: 'Daylight Factor & Views Analysis',
			}
		);

		mouseHandlersRef.current = nextHandlers;

		canvas.addEventListener('mousedown', nextHandlers.handleMouseDown);
		canvas.addEventListener('mousemove', nextHandlers.handleMouseMove);
		canvas.addEventListener('mouseup', nextHandlers.handleMouseUp);
		canvas.addEventListener('mouseleave', nextHandlers.handleMouseLeave);
	}, [sceneInitialized, sceneVersion, onDataChange, workflowData, activeSection]);
	useEffect(() => {
		if (
			sceneRef.current &&
			ambientLightRef.current &&
			directionalLightRef.current &&
			rendererRef.current &&
			orthoPreviewRendererRef.current
		) {
			// Update main scene lighting
			updateLighting(
				sceneRef.current,
				ambientLightRef.current,
				directionalLightRef.current,
				rendererRef.current,
				starsRef.current,
				currentSunPositionRef,
				currentDate,
				currentTime,
				latitude,
				longitude
			);
			updateSunPositionVisualization(sceneRef.current, currentSunPositionRef.current);
			// Update orthographic preview lighting
			updateLighting(
				orthoPreviewRef.current.previewScene,
				ambientLightRef.current,
				directionalLightRef.current,
				orthoPreviewRendererRef.current,
				null,
				currentSunPositionRef,
				currentDate,
				currentTime,
				latitude,
				longitude
			);
		}
	}, [currentDate, currentTime, latitude, longitude, sceneInitialized]);

	useEffect(() => {
		if (
			!cameraRef.current ||
			!sceneRef.current ||
			!mouseHandlersRef.current ||
			!rendererRef.current
		) {
			return;
		}

		if (isHoleCreationMode) {
			return;
		}

		const camera = cameraRef.current;
		const scene = sceneRef.current;
		const mouseHandlers = mouseHandlersRef.current;
		const canvas = rendererRef.current.domElement;

		const clickHandlerFn = clickHandler.createClickHandler(camera, scene);
		const handleClick = (event: MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();

			clickHandlerFn(event, mouseHandlers.getIsRotating());
		};

		canvas.addEventListener('click', handleClick, { passive: false });
		canvas.addEventListener('contextmenu', handleClick, { passive: false });

		return () => {
			canvas.removeEventListener('click', handleClick);
			canvas.removeEventListener('contextmenu', handleClick);
		};
	}, [isHoleCreationMode, clickHandler, sceneInitialized, sceneVersion]);

	// Load saved view features (landmarks and painted areas) when scene is initialized
	useEffect(() => {
		if (!sceneInitialized || !sceneRef.current) return;

		loadViewFeaturesFromStorage(sceneRef.current).catch(error => {
			console.error('Failed to load view features from storage:', error);
		});
	}, [sceneInitialized, sceneVersion]);

	useEffect(() => {
		getLocation();
	}, [getLocation]);

	const handleLevelClick = (levelIndex: number) => {
		if (!buildingGraph || !orthoPreviewRef.current) return;

		const level = buildingGraph.data.levels[levelIndex];
		if (!level) return;

		orthoPreviewRef.current.updateZPosition(levelIndex, level.height, sceneRef.current!);
		setCurrentLevelIndex((prevIndex) => (prevIndex === levelIndex ? null : levelIndex));
	};

	const {
		canvasContainer,
		previewCanvasContainer,
		closeButton,
		closeIcon,
		mainCanvas,
		compassContainer,
		previewCanvas,
		levelButtonsContainer,
		pausedSceneContainer,
		hiddenContainer,
	} = styles;

	if (error) {
		return (
			<WeaveBanner
				variant="warning"
				text={error || 'An unexpected error occurred while loading the scene.'}
			/>
		);
	}

	const instructionsText: {
		[key: string]: { title: string; text: JSX.Element };
	} = {
		'windows-openings': {
			title: 'Windows & Openings Analysis Guidance',
			text: (
				<>
					<p className={styles.guidanceHeading}>Managing windows and openings:</p>
					<ul className={styles.instructionsList}>
						<li>
							Click the windows/openings icon in the toolbar.
						</li>
						<li>
							Configure your preferred snapping interval and opening size.
						</li>
						<li>
							<strong>To add windows:</strong> Click the &quot;Add windows&quot; button, then click and drag across a wall. A live preview shows the pending window row, and releasing the mouse places all previews.
						</li>
						<li>
							<strong>To delete windows:</strong> Click the &quot;Delete windows&quot; button, then click on windows you want to remove.
						</li>
					</ul>
				</>
			),
		},
		'daylight-views': {
			title: 'Sunlight & Views Analysis Guidance',
			text: (
				<>
					<p className={styles.guidanceHeading}>Daily or facade sunlight analysis:</p>
					<ul className={styles.instructionsList}>
						<li>
							Select a building by clicking on it in the 3D view.
						</li>
						<li>
							Click the &quot;Sunlight Analysis&quot; icon in the toolbar.
						</li>
						<li>
							Run daily sunlight analysis or facade sunlight analysis.
						</li>
						<li>
							Results will be visualized on the building and displayed in the &quot;Sunlight Score&quot; menu.
						</li>
					</ul>
					<p className={styles.guidanceHeading}>Detailed sunlight analysis for grid points:</p>
					<ul className={styles.instructionsList}>
						<li>
							<strong>Prerequisite:</strong> Complete daily sunlight analysis first.
						</li>
						<li>
							Click on the sunlight grid in the 3D view and select a minimum of 3 grid points.
						</li>
						<li>
							Click &quot;Detailed sunlight for selected grid points&quot; button.
						</li>
						<li>
							Results will be visualized on the building and displayed in the &quot;Sunlight Score&quot; menu.
						</li>
					</ul>
					<p className={styles.guidanceHeading}>View analysis for windows:</p>
					<ul className={styles.instructionsList}>
						<li>
							Select a building by clicking on it in the 3D view.
						</li>
						<li>
							Click the &quot;View Analysis&quot; icon in the toolbar.
						</li>
						<li>
							Click &quot;Run view analysis for all windows&quot; or click individual windows for specific analysis.
						</li>
						<li>
							Results will be visualized on the building and displayed in the &quot;View Score&quot; menu.
						</li>
						<li>
							<strong>Optional:</strong> For more accurate results, click the &quot;Settings&quot; icon in the toolbar and select &quot;Load terrain texture&quot;.
						</li>
						<li>
							After analysis, click the cyan view plane. Use <strong>E</strong> to tilt (rotate), <strong>W</strong> to drag (translate), or right-click to toggle between rotate and translate.
						</li>
					</ul>
				</>
			),
		},
	};

	return (
		<>
			<SkeletonLoader title="Loading 3D Scene..." loading={!sceneInitialized} />
			{sceneInitialized && (
				<div className={styles.instructions}>
					<WeaveAccordion label={instructionsText[tab.id].title} indicatorposition="right">
						<div className={styles.instructionsText}>
							{instructionsText[tab.id].text}
						</div>
					</WeaveAccordion>
				</div>
			)}
			<div
				className={`${areEventsPaused ? pausedSceneContainer : ''}${sceneInitialized ? '' : hiddenContainer}`}
			>
				<ControlPanel
					latitude={latitude}
					setLatitude={setLatitude}
					longitude={longitude}
					setLongitude={setLongitude}
					sceneRef={sceneRef}
					selectedBuildingId={selectedBuildingRef.current?.uuid ?? null}
					workflowProps={{ activeSection, workflowData, onDataChange, sectionLabel: 'Daylight Factor & Views Analysis' }
					}
				/>

				<div className={canvasContainer} ref={canvasContainerRef}>
					<ThreeControls
						calculateDailySunlightForRooms={
							dailySunlight.calculateDailySunlightForRooms
						}
						calculateDailySunlightForSelectedGridPoints={
							dailySunlight.calculateDailySunlightForSelectedGridPoints
						}
						calculatingDailySunlight={dailySunlight.calculatingDailySunlight}
						onMetricChange={dailySunlight.changeSunlightVisualizationMetric}
						clearAllVisualizations={dailySunlight.clearAllVisualizations}
						scene={sceneRef.current!}
						workflowProps={{
							activeSection,
							workflowData,
							onDataChange,
							sectionLabel: 'Daylight Factor & Views Analysis',
						}}
						previewActive={showOrthoPreview}
						calculateFacadeSunlight={async () => {
							if (
								selectedBuildingRef.current &&
								buildingGraph &&
								latitude !== null &&
								longitude !== null
							) {
								await facadeSunlight.calculateFacadeSunlight(
									selectedBuildingRef.current,
									buildingGraph,
									latitude,
									longitude
								);
							}
						}}
						clearFacadeVisualization={facadeSunlight.clearFacadeVisualization}
						calculatingFacadeSunlight={facadeSunlight.calculatingFacadeSunlight}
						facadeSunlightEnabled={selectedBuildingRef.current !== null}
						startFacadeAnimation={async () => {
							if (
								selectedBuildingRef.current &&
								buildingGraph &&
								latitude !== null &&
								longitude !== null
							) {
								await facadeSunlight.startFacadeAnimation(
									selectedBuildingRef.current,
									buildingGraph,
									latitude,
									longitude
								);
							}
						}}
						calculateSingleDay={async (dayIndex: number) => {
							if (
								selectedBuildingRef.current &&
								buildingGraph &&
								latitude !== null &&
								longitude !== null
							) {
								await facadeSunlight.calculateSingleDay(
									selectedBuildingRef.current,
									buildingGraph,
									latitude,
									longitude,
									dayIndex
								);
							}
						}}
						stopFacadeAnimation={facadeSunlight.stopFacadeAnimation}
						isAnimating={facadeSunlight.isAnimating}
						startDailyAnimation={dailySunlight.startDailyAnimation}
						stopDailyAnimation={dailySunlight.stopDailyAnimation}
						isAnimatingDaily={dailySunlight.isAnimating}
						calculateSingleDayDaily={dailySunlight.calculateSingleDay}
						currentDate={currentDate}
						setCurrentDate={setCurrentDate}
						currentTime={currentTime}
						setCurrentTime={setCurrentTime}
						latitude={latitude}
						setLatitude={setLatitude}
						longitude={longitude}
						setLongitude={setLongitude}
						sceneRef={sceneRef}
						rendererRef={rendererRef}
						cameraRef={cameraRef}
						canvasRef={threeCanvasRef}
						currentSunPositionRef={currentSunPositionRef}
						onBeforeRunPhotonMapping={handleBeforeRunPhotonMapping}
						onAfterClearPhotons={handleAfterClearPhotons}
						tab={tab}
						selectedBuildingId={selectedBuildingRef.current?.uuid ?? null}
						availableFloorIndices={
							buildingGraph?.data.levels?.map((_level, index) => index) ?? []
						}
					/>
					<StatusMessage />
					{tab.id === 'daylight-views' && selectedViewPlaneStats && (
						<SelectedViewPlaneStatsPanel
							stats={selectedViewPlaneStats}
							visualizationMode={viewPlaneVisualizationMode}
							onVisualizationModeChange={setViewPlaneVisualizationMode}
							blockingMode={viewPlaneBlockingMode}
							onBlockingModeChange={setViewPlaneBlockingMode}
							categoryFilter={viewPlaneCategoryFilter}
							onCategoryFilterChange={setViewPlaneCategoryFilter}
							inspectorResult={viewPlaneInspector}
							sampleScale={viewPlaneSampleScale}
							onSampleScaleChange={setViewPlaneSampleScale}
							scene={sceneRef.current || undefined}
							workflowData={workflowData}
							onDataChange={onDataChange}
							activeSection={activeSection}
							onClose={() => {
								if (sceneRef.current) {
									removeViewLineById(selectedViewPlaneStats.windowId, sceneRef.current);
								}
								window.dispatchEvent(new CustomEvent('__viewAnalysisClearVisualization__'));
							}}
						/>
					)}
					<FullScreenButton element={canvasContainerRef.current} />
					<canvas
						ref={threeCanvasRef}
						id="three-canvas"
						className={mainCanvas}
						style={{ height: `${canvasHeight}px` }}
					/>
					<SunlightTooltip />
					<FacadeTooltip />
					<SunlightBarChart visible={showBarChart} />
					<div
						className={previewCanvasContainer}
						style={{ display: showOrthoPreview ? 'block' : 'none' }}
					>
						<WeaveIconButton
							className={closeButton}
							variant="outlined"
							onClick={handleClearPreview}
						>
							<Icon
								className={closeIcon}
								name="weave-close"
							/>
						</WeaveIconButton>
						<div className={compassContainer}>
							<FormaCompass />
						</div>
						<div className={levelButtonsContainer}>
							{buildingGraph?.data.levels.map((_level, i) => i).reverse().map((i) => (
								<WeaveButton
									key={i}
									onClick={() => handleLevelClick(i)}
									variant={currentLevelIndex === i ? 'solid' : 'outlined'}
								>
									{i}
								</WeaveButton>
							))}
						</div>
						<canvas
							ref={orthoPreviewCanvasRef}
							id="ortho-preview-canvas"
							className={previewCanvas}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default ThreeScene;
